import { useEffect, useRef, useState } from 'react';
import { CONTACT } from '../content';
import { prefersReducedMotion } from '../hooks';
import { composeInquiry, gmailUrl, isValidEmail, mailtoUrl, outlookUrl } from '../lib/inquiry';
import { imgUrl, srcSet } from '../theme';
import Reveal from './Reveal';
import { ArrowRight, Check, Copy, Mail, MapPin, Phone } from './Icons';
import './Contact.css';

const EMPTY = { name: '', email: '', company: '', interests: [], timeline: '', message: '' };
const REQUIRED = ['name', 'email', 'message'];

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please tell us your name.';
  if (!isValidEmail(form.email)) errors.email = 'Please enter a valid email address.';
  if (!form.message.trim()) errors.message = 'A sentence or two about the project helps a lot.';
  return errors;
}

// Hands the draft to the visitor's default mail app.
function openMailApp(url) {
  const link = document.createElement('a');
  link.href = url;
  link.click();
}

function Field({ id, label, optional = false, error, children }) {
  return (
    <div className={`field${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {optional && <span className="field__optional"> · optional</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}

export default function Contact({ theme, prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [draft, setDraft] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cardHeight, setCardHeight] = useState(0);
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const doneRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const messageRef = useRef(null);
  const fieldRefs = { name: nameRef, email: emailRef, message: messageRef };

  // "Start a project" in the hero carries the email here.
  useEffect(() => {
    if (!prefill) return;
    setDraft(null);
    if (prefill.email) setForm((f) => ({ ...f, email: prefill.email }));
    sectionRef.current.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    requestAnimationFrame(() => (prefill.email ? nameRef : emailRef).current?.focus({ preventScroll: true }));
  }, [prefill]);

  useEffect(() => {
    if (draft) doneRef.current?.focus({ preventScroll: true });
  }, [draft]);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors(({ [key]: _, ...rest }) => rest);
    }
  };

  const toggleInterest = (item) =>
    update('interests', form.interests.includes(item) ? form.interests.filter((i) => i !== item) : [...form.interests, item]);

  const submit = (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    const firstInvalid = REQUIRED.find((key) => found[key]);
    if (firstInvalid) {
      fieldRefs[firstInvalid].current.focus();
      return;
    }
    const next = composeInquiry({ to: CONTACT.email, ...form });
    // Keep the card the same height so the confirmation doesn't jump the page.
    setCardHeight(formRef.current.parentElement.offsetHeight);
    setDraft(next);
    setCopied(false);
    openMailApp(mailtoUrl(next));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`To: ${draft.to}\nSubject: ${draft.subject}\n\n${draft.body}`);
      setCopied(true);
    } catch (err) {
      setCopied(false);
    }
  };

  const describedBy = (key) => (errors[key] ? `cf-${key}-error` : undefined);

  return (
    <section id="contact" ref={sectionRef} className="contact tone-dark" data-nav="dark">
      <div className="contact__bg" aria-hidden="true">
        <img
          src={imgUrl(theme.cta.name, 1920)}
          srcSet={srcSet(theme.cta.name, [1280, 1920, 2560])}
          sizes="100vw"
          loading="lazy"
          decoding="async"
          alt=""
          style={{ objectPosition: theme.cta.position }}
        />
      </div>

      <div className="container contact__grid">
        <Reveal className="contact__copy">
          <span className="eyebrow">Contact</span>
          <h2 className="h2">
            Tell us about your <em>mission</em>.
          </h2>
          <p className="lead">
            Share a few details and we’ll get back to you with next steps. The form prepares an email for you —
            nothing is sent until you press send.
          </p>
          <ul className="contact__direct">
            <li>
              <Mail />
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </li>
            <li>
              <Phone />
              <a href={CONTACT.phone.href}>{CONTACT.phone.display}</a>
            </li>
            <li>
              <MapPin />
              <span>{CONTACT.location}</span>
            </li>
          </ul>
        </Reveal>

        <Reveal className="cform" delay={120} style={draft ? { minHeight: cardHeight } : undefined}>
          {draft ? (
            <div ref={doneRef} className="cform__done" tabIndex={-1} role="status">
              <span className="cform__done-icon">
                <Check width={24} height={24} />
              </span>
              <h3 className="h3">Your email draft is ready</h3>
              <p className="body-2">
                We opened a pre-filled message to <b>{draft.to}</b> in your email app. Give it a quick look and hit
                send — we’ll take it from there.
              </p>
              <div className="cform__fallback">
                <span className="cform__fallback-label">Didn’t open? Use one of these instead:</span>
                <div className="cform__fallback-btns">
                  <a className="btn btn-ghost btn-sm" href={gmailUrl(draft)} target="_blank" rel="noopener noreferrer">
                    Gmail
                  </a>
                  <a className="btn btn-ghost btn-sm" href={outlookUrl(draft)} target="_blank" rel="noopener noreferrer">
                    Outlook
                  </a>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
                    {copied ? <Check width={16} height={16} /> : <Copy width={16} height={16} />}
                    {copied ? 'Copied' : 'Copy text'}
                  </button>
                </div>
              </div>
              <button type="button" className="link-arrow cform__edit" onClick={() => setDraft(null)}>
                Edit details <ArrowRight />
              </button>
            </div>
          ) : (
            <form ref={formRef} className="cform__form" noValidate onSubmit={submit}>
              <div className="cform__row">
                <Field id="cf-name" label="Name" error={errors.name}>
                  <input
                    ref={nameRef}
                    id="cf-name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy('name')}
                    required
                  />
                </Field>
                <Field id="cf-email" label="Work email" error={errors.email}>
                  <input
                    ref={emailRef}
                    id="cf-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                    required
                  />
                </Field>
              </div>

              <Field id="cf-company" label="Company" optional>
                <input
                  id="cf-company"
                  name="company"
                  autoComplete="organization"
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                />
              </Field>

              <fieldset className="cform__group">
                <legend>What are you building?</legend>
                <div className="cform__chips">
                  {CONTACT.interests.map((item) => {
                    const on = form.interests.includes(item);
                    return (
                      <label key={item} className={`chip${on ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleInterest(item)} />
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <fieldset className="cform__group">
                <legend>Timeline</legend>
                <div className="cform__chips">
                  {CONTACT.timelines.map((t) => (
                    <label key={t} className={`chip${form.timeline === t ? ' is-on' : ''}`}>
                      <input
                        type="radio"
                        name="timeline"
                        value={t}
                        checked={form.timeline === t}
                        onChange={() => update('timeline', t)}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Field id="cf-message" label="Project details" error={errors.message}>
                <textarea
                  ref={messageRef}
                  id="cf-message"
                  name="message"
                  rows={5}
                  placeholder="What are you building, who is it for, and where are you today?"
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={describedBy('message')}
                  required
                />
              </Field>

              <div className="cform__submit">
                <button type="submit" className="btn btn-primary">
                  <Mail width={18} height={18} /> Send email
                </button>
                <p className="cform__note">
                  Please review the information before sending. It will open your email app with everything filled in.
                </p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
